import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useLang } from "../contexts/LangContext";
import { Users, Cpu, CreditCard, AlertTriangle, TrendingUp, Clock, MapPin } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => new Intl.NumberFormat("sq-AL").format(Math.round(n || 0));

function KPICard({ icon: Icon, label, value, sub, color = "slate", accent }) {
    const colors = {
        red: "border-l-[#E7212E] text-[#E7212E]",
        green: "border-l-emerald-500 text-emerald-600",
        amber: "border-l-amber-500 text-amber-600",
        blue: "border-l-blue-500 text-blue-600",
        slate: "border-l-slate-400 text-slate-600",
    };
    return (
        <div className={`bg-white border border-slate-200 border-l-4 ${colors[accent || color]} rounded p-5`}>
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">{label}</p>
                    <p className="text-2xl font-bold font-mono text-slate-900">{value}</p>
                    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
                </div>
                <div className={`p-2.5 rounded bg-slate-50`}>
                    <Icon size={18} className={colors[accent || color].split(" ")[1]} />
                </div>
            </div>
        </div>
    );
}

export default function DashboardPage() {
    const { t } = useLang();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const { data } = await axios.get(`${API}/dashboard/stats`, { withCredentials: true });
            setStats(data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="p-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="bg-white border border-slate-200 rounded p-5 h-24 animate-pulse bg-slate-100" />
                    ))}
                </div>
            </div>
        );
    }

    const chartData = stats?.revenue?.chart_data || [];

    return (
        <div className="p-6 space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                    <MapPin size={20} className="text-[#E7212E]" />
                    {t("dashboard")}
                </h1>
                <p className="text-sm text-slate-500 mt-0.5">
                    {new Date().toLocaleDateString("sq-AL", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
                </p>
            </div>

            {/* KPI Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="kpi-grid">
                <KPICard
                    icon={Users}
                    label={t("total_clients")}
                    value={stats?.clients?.total || 0}
                    sub={`${stats?.clients?.active || 0} aktiv · ${stats?.clients?.debtor || 0} borxhli`}
                    accent="blue"
                />
                <KPICard
                    icon={Cpu}
                    label={t("active_devices")}
                    value={stats?.devices?.active || 0}
                    sub={`${stats?.devices?.stock || 0} stok · ${stats?.devices?.total || 0} gjithsej`}
                    accent="green"
                />
                <KPICard
                    icon={AlertTriangle}
                    label={t("unpaid_devices")}
                    value={stats?.subscriptions?.unpaid || 0}
                    sub="Abonime të skaduara"
                    accent="red"
                />
                <KPICard
                    icon={CreditCard}
                    label={t("monthly_revenue")}
                    value={`${fmt(stats?.revenue?.monthly)} Lek`}
                    sub="Muaji aktual"
                    accent="green"
                />
                <KPICard
                    icon={Clock}
                    label={t("expiring_soon")}
                    value={stats?.subscriptions?.expiring_soon || 0}
                    sub="Brenda 15 ditëve"
                    accent="amber"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Revenue Chart */}
                <div className="lg:col-span-2 bg-white border border-slate-200 rounded p-5">
                    <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                        <TrendingUp size={15} className="text-[#E7212E]" />
                        {t("monthly_chart")}
                    </h3>
                    <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                            <Tooltip
                                formatter={(v) => [`${fmt(v)} Lek`, t("revenue")]}
                                contentStyle={{ border: "1px solid #E2E8F0", borderRadius: "4px", fontSize: "12px" }}
                            />
                            <Bar dataKey="revenue" fill="#E7212E" radius={[2, 2, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Quick Stats */}
                <div className="bg-white border border-slate-200 rounded p-5">
                    <h3 className="text-sm font-semibold text-slate-700 mb-4">Statistika të Shpejta</h3>
                    <div className="space-y-3">
                        {[
                            { label: "Klientë Aktiv", value: stats?.clients?.active || 0, color: "text-emerald-600" },
                            { label: "Klientë Borxhli", value: stats?.clients?.debtor || 0, color: "text-rose-600" },
                            { label: "Abonime Aktive", value: stats?.subscriptions?.active || 0, color: "text-blue-600" },
                            { label: "Pajisje Stok", value: stats?.devices?.stock || 0, color: "text-amber-600" },
                        ].map((item, i) => (
                            <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                                <span className="text-sm text-slate-600">{item.label}</span>
                                <span className={`text-sm font-bold font-mono ${item.color}`}>{item.value}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Recent Payments */}
            {stats?.recent_payments?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded overflow-hidden">
                    <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-slate-700">{t("recent_payments")}</h3>
                        <button onClick={() => navigate("/payments")} className="text-xs text-[#E7212E] hover:underline">
                            Shiko të gjitha
                        </button>
                    </div>
                    <table className="w-full crm-table" data-testid="recent-payments-table">
                        <thead>
                            <tr>
                                <th>{t("client")}</th>
                                <th>{t("amount")}</th>
                                <th>{t("method")}</th>
                                <th>{t("date")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stats.recent_payments.map((p, i) => (
                                <tr key={p.id || i}>
                                    <td className="font-medium text-slate-900">{p.client_name}</td>
                                    <td className="font-mono font-medium text-emerald-600">{fmt(p.amount)} Lek</td>
                                    <td className="text-slate-500 capitalize">{p.method}</td>
                                    <td className="text-slate-500">{p.payment_date?.substring(0, 10)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
